/**
 * Contact and inquiry.
 *
 * The form is the channel. What happens to a message afterwards depends on
 * whether a transport is configured on the server, and the confirmation says
 * which it was — a page that says "we'll be in touch" when nothing left the
 * building is a page that lies.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api, useQuery } from '../api.js';
import { PublicFooter, PublicHeader } from './PublicPage.js';

type Kind = 'sales' | 'support' | 'privacy' | 'general';

const KINDS: [Kind, string, string][] = [
  ['sales', 'Schools and 141 programs', 'Pricing for a fleet, a migration, or a question before you commit.'],
  ['support', 'Help with an account', 'Something is not working, or you cannot get in.'],
  ['privacy', 'Privacy and records', 'A data request, an export, or a question about what we hold.'],
  ['general', 'Anything else', 'Partnerships, press, or none of the above.'],
];

const OPERATIONS: [string, string][] = [
  ['', 'Not sure yet'],
  ['freelance', 'Freelance CFI'],
  ['part61', 'Part 61 school'],
  ['part141', 'Part 141 certificated program'],
];

export function Contact(): JSX.Element {
  const [params] = useSearchParams();
  const initial = KINDS.map(([k]) => k).includes(params.get('about') as Kind)
    ? (params.get('about') as Kind)
    : 'sales';

  const site = useQuery<{ supportEmail: string | null; delivers: boolean }>('/site');

  const [kind, setKind] = useState<Kind>(initial);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [operation, setOperation] = useState(params.get('plan') === 'program' ? 'part141' : '');
  const [fleetSize, setFleetSize] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<'recorded' | 'queued' | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await api.post<{ status: 'recorded' | 'queued' }>('/inquiries', {
        kind, name, email, organisation, operation, fleetSize, message, website,
        source: `contact:${kind}`,
      });
      setSent(res.status);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="lp">
        <PublicHeader />
        <div className="lp__doc">
          <h1 className="lp__doct">Message received</h1>
          <p className="lp__docl">
            {sent === 'queued'
              ? `Thanks, ${name.split(' ')[0] || 'and welcome'}. It is on its way to us, and we answer to ${email} within one business day.`
              : `Thanks, ${name.split(' ')[0] || 'and welcome'}. Your message is recorded against this instance. `
                + 'This deployment has no mail transport configured, so nobody has been paged yet — '
                + 'if it is urgent, mention it again once you are signed in.'}
          </p>
          <div className="lp__actions" style={{ justifyContent: 'flex-start', marginTop: 26 }}>
            <Link to="/" className="btn btn--primary" style={{ textDecoration: 'none' }}>Back to the site</Link>
            <Link to="/support" className="btn btn--tinted" style={{ textDecoration: 'none' }}>Read the help centre</Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="lp">
      <PublicHeader />
      <div className="lp__doc">
        <h1 className="lp__doct">Talk to us</h1>
        <p className="lp__docl">
          Tell us what you fly and how you are certificated, and we will answer with something
          specific rather than a brochure.
        </p>

        <div className="lp__contact">
          <form className="card lp__card" onSubmit={submit} style={{ padding: 24 }}>
            <div className="field">
              <label className="field__label" htmlFor="c-kind">What is this about?</label>
              <select id="c-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                {KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <div className="field__hint">{KINDS.find(([k]) => k === kind)?.[2]}</div>
            </div>

            <div className="lp__pair">
              <div className="field">
                <label className="field__label" htmlFor="c-name">Your name</label>
                <input id="c-name" className="input" value={name} required minLength={2} maxLength={80}
                  autoComplete="name" onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="c-email">Email</label>
                <input id="c-email" className="input" type="email" value={email} required maxLength={160}
                  autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            {kind === 'sales' && (
              <>
                <div className="lp__pair">
                  <div className="field">
                    <label className="field__label" htmlFor="c-org">School or operation</label>
                    <input id="c-org" className="input" value={organisation} maxLength={120}
                      autoComplete="organization" onChange={(e) => setOrganisation(e.target.value)} />
                  </div>
                  <div className="field">
                    <label className="field__label" htmlFor="c-fleet">Aircraft on the line</label>
                    <input id="c-fleet" className="input" value={fleetSize} maxLength={40}
                      placeholder="e.g. 6" onChange={(e) => setFleetSize(e.target.value)} />
                  </div>
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="c-op">How are you certificated?</label>
                  <select id="c-op" className="select" value={operation} onChange={(e) => setOperation(e.target.value)}>
                    {OPERATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              </>
            )}

            <div className="field">
              <label className="field__label" htmlFor="c-msg">Your message</label>
              <textarea id="c-msg" className="textarea" rows={6} value={message} required
                minLength={10} maxLength={4000} onChange={(e) => setMessage(e.target.value)} />
              <div className="field__hint">{message.trim().length} of 4,000 characters.</div>
            </div>

            {/* Left empty by people, filled in by most bots. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="c-website">Website</label>
              <input id="c-website" tabIndex={-1} autoComplete="off" value={website}
                onChange={(e) => setWebsite(e.target.value)} />
            </div>

            {error && <div className="note" style={{ color: 'var(--sys-red)' }}>{error}</div>}

            <button className="btn btn--primary btn--block" disabled={busy} style={{ marginTop: 16 }}>
              {busy ? 'Sending…' : 'Send message'}
            </button>
            <p className="note">
              We use what you send here to answer you, and for nothing else. See the{' '}
              <Link to="/privacy">privacy statement</Link>.
            </p>
          </form>

          <aside>
            <div className="card lp__card">
              <div className="lp__cardt" style={{ marginTop: 0 }}>Faster than writing to us</div>
              <div className="lp__bullet"><span /><span>
                <Link to="/support">The help centre</Link> covers the grid, the portal, maintenance and billing.
              </span></div>
              <div className="lp__bullet"><span /><span>
                <Link to="/status">System status</Link> shows whether the problem is ours.
              </span></div>
              <div className="lp__bullet"><span /><span>
                Trying it is free — <Link to="/signup">start a school</Link> and look around before you ask.
              </span></div>
            </div>

            <div className="card lp__card" style={{ marginTop: 14 }}>
              <div className="lp__cardt" style={{ marginTop: 0 }}>How we answer</div>
              <div className="lp__cardb">
                {site.data?.delivers === false
                  ? 'This deployment has no mail transport configured, so messages are recorded here rather than delivered. '
                    + 'Everything you send is kept, and nothing is dropped.'
                  : 'One business day, from a person, to the address you give us.'}
              </div>
              {site.data?.supportEmail && (
                <div className="lp__cardb">
                  You can also write to{' '}
                  <a href={`mailto:${site.data.supportEmail}`}>{site.data.supportEmail}</a>.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
